import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/app/Header";
import SignupNudgeModal from "../components/app/SignupNudgeModal";
import { api } from "../lib/api";
import { Calendar, MapPin, Sparkles, Loader2, Music, Users, ChevronRight } from "lucide-react";

/**
 * /festivals — Festival hub page.
 *
 * Lists every UK festival in the next 6 months in date order. Each card
 * shows when the festival starts (D-N countdown), where it is, the
 * audience tag, and a tap-through to the per-festival page where we
 * surface the actual campsites nearby.
 *
 * Why this exists: campers heading to Download / Glasto / Latitude
 * currently have to flip between three apps to find an off-site campsite.
 * This hub collapses that into "pick your festival → see vetted sites
 * sorted by distance and audience-fit." £1.99 of relief.
 */

const AUDIENCE_PILLS = {
  family: { label: "Family-friendly", color: "bg-[#16a34a] text-white" },
  adults: { label: "Adults / 18+", color: "bg-[#9333ea] text-white" },
  rowdy: { label: "Late-night crowd", color: "bg-[#dc2626] text-white" },
  boutique: { label: "Boutique · chill", color: "bg-[#0891b2] text-white" },
  mixed: { label: "All ages", color: "bg-[#0e0e0e] text-white" },
};

function formatDateRange(start, end) {
  try {
    const s = new Date(start);
    const e = new Date(end);
    const sLabel = s.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const eLabel = e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    return `${sLabel} → ${eLabel}`;
  } catch {
    return `${start} → ${end}`;
  }
}

function CountdownBadge({ days }) {
  if (days == null) return null;
  if (days < 0) return null;
  const text = days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days} days`;
  const tone = days <= 14 ? "bg-[#ff4500] text-white" : "bg-[#0e0e0e] text-white";
  return (
    <span className={`inline-flex items-center gap-1 ${tone} px-2 py-1 text-[10px] mono uppercase tracking-wider`}>
      <Sparkles className="w-3 h-3" strokeWidth={2.2} /> {text}
    </span>
  );
}

export default function Festivals() {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/festivals").then(({ data }) => {
      if (alive) setFestivals(data?.festivals || []);
    }).catch(() => { /* swallow */ })
    .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="festivals-page">
      <Header active="festivals" />

      {/* HERO */}
      <div className="bg-[#0e0e0e] text-white border-b-2 border-[#ff4500]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
          <div className="eyebrow text-[#ff4500] mb-3 inline-flex items-center gap-2">
            <Music className="w-3 h-3" strokeWidth={2.4} /> Festival camping · UK · next 6 months
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-4">
            Stop scrolling. <br />
            <span className="text-[#ff4500]">Find your festival pitch.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
            Every major UK festival in the next six months, with vetted campsites nearby —
            sorted by distance, tagged by who they suit. Family weekenders, late-night raves,
            boutique folk gatherings. Pick your festival, we'll do the rest.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm mono text-white/60 uppercase tracking-wider">
            <Users className="w-4 h-4" /> {loading ? "…" : `${festivals.length} festivals listed`}
          </div>
        </div>
      </div>

      {/* LIST */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#555]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading festivals…
          </div>
        ) : festivals.length === 0 ? (
          <div className="text-center py-20 text-[#555]">
            No festivals in the next six months — check back closer to summer.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="festivals-grid">
            {festivals.map((f, i) => {
              const pill = AUDIENCE_PILLS[f.audience] || AUDIENCE_PILLS.mixed;
              return (
                <Link
                  key={f.slug}
                  to={`/festivals/${f.slug}`}
                  className="group block border hairline bg-white hover:shadow-[0_2px_0_0_#0e0e0e] hover:-translate-y-0.5 transition-all duration-200"
                  data-testid={`festival-card-${i}`}
                >
                  {/* Hero strip — solid colour band keyed to the festival */}
                  <div
                    className="h-2"
                    style={{ background: f.hero_color || "#ff4500" }}
                  />
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <CountdownBadge days={f.days_until} />
                      <span className={`${pill.color} px-2 py-1 text-[10px] mono uppercase tracking-wider`}>
                        {pill.label}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl tracking-tight leading-tight mb-2 group-hover:text-[#ff4500] transition-colors">
                      {f.name}
                    </h2>
                    <div className="text-xs mono text-[#666] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                      <Calendar className="w-3 h-3" /> {formatDateRange(f.start_date, f.end_date)}
                    </div>
                    <div className="text-xs mono text-[#666] uppercase tracking-wider flex items-center gap-1.5 mb-3">
                      <MapPin className="w-3 h-3" /> {f.location_label}
                    </div>
                    {f.description && (
                      <p className="text-sm text-[#333] leading-relaxed mb-4 line-clamp-3">
                        {f.description}
                      </p>
                    )}
                    <div className="inline-flex items-center gap-1 text-sm font-display text-[#ff4500] group-hover:gap-2 transition-all">
                      See nearby campsites <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <SignupNudgeModal />
    </div>
  );
}
